import classNames from 'classnames'
import { type ChangeEvent, useLayoutEffect, useRef, type KeyboardEvent, useState, useEffect } from 'react'

import { onRenderMarkdown, onToasting } from '@/app/utils'

import { MarkdownTemplates } from './MarkdownTemplates/MarkdownTemplates'
import style from './TextArea.module.scss'

interface IProps {
    value: string
    placeholder?: string
    scrollHeight?: number
    onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void
    onEnter?: () => void
    onTouch?: () => void
    disabled?: boolean
    className?: string
    isError?: boolean
    withMarkdown?: boolean
}

export const TextArea = (props: IProps) => {
    const {
        value,
        isError,
        onChange,
        onTouch,
        onEnter,
        placeholder,
        scrollHeight = 20,
        disabled = false,
        className,
        withMarkdown = false
    } = props
    const [isMarkdown, setIsMarkdown] = useState<boolean>(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useLayoutEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'inherit'
            textareaRef.current.style.height = `${Math.max(textareaRef.current.scrollHeight, scrollHeight)}px`
        }
    }, [value, scrollHeight])

    useEffect(() => {
        if (disabled && withMarkdown) {
            setIsMarkdown(true)
        }
    }, [disabled, withMarkdown])

    const onEnterPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        e.preventDefault()
        if (e.key === 'Enter' && !e.shiftKey) {
            if (onEnter) onEnter()
        }
    }

    const onFocus = () => {
        if (onTouch) {
            onTouch()
        }
    }

    const onAddMarkdownTemplate = (template: string) => {
        if (isMarkdown) {
            onToasting({ type: 'info', message: 'Добавить разметку по шаблону можно только в режиме "РАЗМЕТКА"' })
            return
        }
        if (textareaRef.current) {
            textareaRef.current.value = `${value}\n\n${template}`
            const event = {
                target: textareaRef.current,
                currentTarget: textareaRef.current
            } as unknown as React.ChangeEvent<HTMLTextAreaElement>
            onChange(event)
        }
    }

    return (
        <div className={classNames(style.container, isError && style.containerError, className)}>
            {!disabled && withMarkdown && !isMarkdown && (
                <MarkdownTemplates onAddMarkdownTemplate={onAddMarkdownTemplate} />
            )}
            {!disabled && withMarkdown && (
                <div className={style.controls}>
                    <button
                        onClick={() => setIsMarkdown((prev) => !prev)}
                        className={classNames(style.btn, !isMarkdown ? style.active : style.inActive)}
                    >
                        РАЗМЕТКА
                    </button>
                    <button
                        onClick={() => setIsMarkdown((prev) => !prev)}
                        className={classNames(style.btn, isMarkdown ? style.active : style.inActive)}
                    >
                        ВИЗУАЛ
                    </button>
                </div>
            )}
            {!isMarkdown && (
                <textarea
                    className={classNames(style.textarea, withMarkdown && style.textareaWithMarkdown)}
                    placeholder={placeholder}
                    onChange={onChange}
                    onFocus={onFocus}
                    ref={textareaRef}
                    value={value}
                    onKeyUp={onEnterPress}
                    disabled={disabled}
                />
            )}
            {withMarkdown && isMarkdown && (
                <div className={style.markdown} dangerouslySetInnerHTML={{ __html: onRenderMarkdown(value) }} />
            )}
        </div>
    )
}
